import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAddTransaction, useUpdateTransaction } from "../../hooks/investments";
import type { InvestmentTransactionType, TransactionDto } from "../../lib/investments";
import { MoneyInput } from "../forms/MoneyInput";
import { Modal } from "../ui/Modal";

const TRANSACTION_TYPES: InvestmentTransactionType[] = ["BUY", "SELL", "DIVIDEND", "TRANSFER"];

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

interface Props {
  investmentId: string;
  existing?: TransactionDto;
  onClose: () => void;
}

export function TransactionFormModal({ investmentId, existing, onClose }: Props) {
  const { t } = useTranslation();
  const addMutation = useAddTransaction(investmentId);
  const updateMutation = useUpdateTransaction(investmentId);

  const [date, setDate] = useState(existing?.date ?? todayIso());
  const [time, setTime] = useState(existing?.time ?? "");
  const [type, setType] = useState<InvestmentTransactionType>(existing?.type ?? "BUY");
  const [shares, setShares] = useState(existing?.shares ?? "");
  const [priceCents, setPriceCents] = useState<number | null>(existing?.priceCents ?? null);
  const [amountCents, setAmountCents] = useState<number | null>(existing?.amountCents ?? null);
  const [feeCents, setFeeCents] = useState<number | null>(existing?.feeCents ?? 0);
  const [taxCents, setTaxCents] = useState<number | null>(existing?.taxCents ?? 0);
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const isEdit = existing !== undefined;
  const title = isEdit
    ? t("investments.transaction.edit_button")
    : t("investments.transaction.add_button");

  useEffect(() => {
    setDate(existing?.date ?? todayIso());
    setTime(existing?.time ?? "");
    setType(existing?.type ?? "BUY");
    setShares(existing?.shares ?? "");
    setPriceCents(existing?.priceCents ?? null);
    setAmountCents(existing?.amountCents ?? null);
    setFeeCents(existing?.feeCents ?? 0);
    setTaxCents(existing?.taxCents ?? 0);
    setNotes(existing?.notes ?? "");
    setError(null);
  }, [existing]);

  const submitting = addMutation.isPending || updateMutation.isPending;

  async function handleSubmit() {
    setError(null);
    if (amountCents === null) {
      setError(t("investments.errors.invalid_amount"));
      return;
    }
    const trimmedShares = shares.trim();
    const trimmedTime = time.trim();
    const sharesValue = trimmedShares === "" ? null : trimmedShares;
    try {
      if (isEdit && existing) {
        await updateMutation.mutateAsync({
          id: existing.id,
          patch: {
            date,
            time: trimmedTime === "" ? null : trimmedTime,
            type,
            shares: sharesValue,
            priceCents,
            amountCents,
            feeCents: feeCents ?? 0,
            taxCents: taxCents ?? 0,
            notes: notes.trim() === "" ? null : notes.trim(),
          },
        });
      } else {
        await addMutation.mutateAsync({
          date,
          time: trimmedTime === "" ? undefined : trimmedTime,
          type,
          shares: sharesValue,
          priceCents,
          amountCents,
          feeCents: feeCents ?? 0,
          taxCents: taxCents ?? 0,
          notes: notes.trim() === "" ? undefined : notes.trim(),
        });
      }
      onClose();
    } catch {
      setError(t("investments.errors.validation"));
    }
  }

  return (
    <Modal open onClose={onClose} title={title}>
      <div className="space-y-3">
        {isEdit && existing?.source === "IMPORT" && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {t("investments.transaction.edit_import_warning")}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-sm">{t("investments.transaction.form_date_label")}</span>
            <input
              type="date"
              className="block mt-1 w-full border rounded px-2 py-1"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </label>
          <label className="block">
            <span className="text-sm">{t("investments.transaction.form_time_label")}</span>
            <input
              type="text"
              placeholder="HH:MM:SS"
              className="block mt-1 w-full border rounded px-2 py-1 font-mono text-sm"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              pattern="\d{2}:\d{2}:\d{2}"
            />
          </label>
        </div>

        <label className="block">
          <span className="text-sm">{t("investments.transaction.form_type_label")}</span>
          <select
            className="block mt-1 w-full border rounded px-2 py-1"
            value={type}
            onChange={(e) => setType(e.target.value as InvestmentTransactionType)}
          >
            {TRANSACTION_TYPES.map((tt) => (
              <option key={tt} value={tt}>
                {t(`investments.transaction.type_${tt}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm">{t("investments.transaction.form_shares_label")}</span>
          <input
            type="text"
            className="block mt-1 w-full border rounded px-2 py-1 font-mono text-sm"
            value={shares}
            onChange={(e) => setShares(e.target.value)}
            placeholder="0.00000000"
            pattern="-?\d+(\.\d{1,8})?"
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label htmlFor="tx-price" className="text-sm">
              {t("investments.transaction.form_price_label")}
            </label>
            <MoneyInput
              valueCents={priceCents}
              onChange={setPriceCents}
              placeholder="100,00"
              className="block mt-1 w-full border rounded px-2 py-1"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="tx-amount" className="text-sm">
              {t("investments.transaction.form_amount_label")}
            </label>
            <MoneyInput
              valueCents={amountCents}
              onChange={setAmountCents}
              placeholder="1000,00"
              className="block mt-1 w-full border rounded px-2 py-1"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label htmlFor="tx-fee" className="text-sm">
              {t("investments.transaction.form_fee_label")}
            </label>
            <MoneyInput
              valueCents={feeCents}
              onChange={setFeeCents}
              placeholder="0,00"
              className="block mt-1 w-full border rounded px-2 py-1"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="tx-tax" className="text-sm">
              {t("investments.transaction.form_tax_label")}
            </label>
            <MoneyInput
              valueCents={taxCents}
              onChange={setTaxCents}
              placeholder="0,00"
              className="block mt-1 w-full border rounded px-2 py-1"
            />
          </div>
        </div>

        <label className="block">
          <span className="text-sm">{t("investments.transaction.form_notes_label")}</span>
          <input
            type="text"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
          />
        </label>

        {error && <div className="text-red-700 text-sm">{error}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border rounded">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded"
          >
            {submitting ? t("common.submitting") : t("common.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
